import React, { useState, useEffect, useRef } from 'react';
import './BuscaExames.css';
import ButtonLink from '../buttonLinkComponent/ButtonLink';
import { handleResetToHome } from '../../utils/buttonLink';
import { IoSearch } from 'react-icons/io5';
import { authenticatedFetch, sendMessage, getApiBaseUrl } from '../../functions/apiCalls/api';
import { ReactComponent as IconHomeDefault } from '../../images/icon-service-home-primary-color.svg';
import { ReactComponent as IconUnitDefault } from '../../images/icon-service-unit-primary-color.svg';
import { ReactComponent as IconHomeAreaLogada } from '../../images/icon-service-home-area-logada.svg';
import { ReactComponent as IconUnitAreaLogada } from '../../images/icon-service-unit-area-logada.svg';



const BuscaExames = ({
    onSearchClick,
    message,
    handleButtonSetMessage,
    client_id,
    marca = 'AM',
    origem = 'chat-web',
    messages = [],
    setMessages,
    localizacao = null,
    operational_system = 'web',
    version_app = '1.0.0',
    buscaExamesMovel = false,
    setRequestId,
    requestImage,
    buttonLink = false,
    buttonLinkText = '',
    onButtonLinkClick,
    showHomeLink = buttonLink
}) => {
    const isAreaLogada = origem === 'area_logada';
    const IconHome = isAreaLogada ? IconHomeAreaLogada : IconHomeDefault;
    const IconUnit = isAreaLogada ? IconUnitAreaLogada : IconUnitDefault;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [visible, setVisible] = useState(false);
    const searchInputRef = useRef(null);
    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedExams, setSelectedExams] = useState([]);
    const [originalExams, setOriginalExams] = useState([]); // Exames que vieram da API original
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [initialMessageCount, setInitialMessageCount] = useState(0);
    const [submitTimeoutId, setSubmitTimeoutId] = useState(null);
    const shouldShowPrices = message?.agendamentoConvenio !== true;

    // Função para formatar valor monetário no padrão brasileiro
    const formatCurrency = (value) => {
        const numericValue = typeof value === 'string' ? parseFloat(value) : value;

        if (isNaN(numericValue)) return 'R$ 0,00';

        return numericValue.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    // Carregar exames pré-existentes da mensagem quando componente for montado
    useEffect(() => {
        if (message?.examesSelecionados?.procedimentos) {
            // Converter formato da API para formato do componente
            const examesConvertidos = message.examesSelecionados.procedimentos.map(exam => ({
                id: exam.id,
                name: exam.nome,
                price: exam.valor,
                // Preservar corretamente a flag de cobertura móvel que veio do backend
                isMobileCoverage:
                    typeof exam.hasMobileCoverage === 'boolean'
                        ? exam.hasMobileCoverage
                        : (typeof exam.isMobileCoverage === 'boolean'
                            ? exam.isMobileCoverage
                            : false),
                isOriginal: true // Marcar como exame original
            }));

            // Carregar os exames pré-existentes no estado
            setSelectedExams(examesConvertidos);
            setOriginalExams(examesConvertidos); // Salvar lista de exames originais

            console.log('Exames pré-existentes carregados:', examesConvertidos);
        }
    }, [message]);

    // Debounce para busca automática
    useEffect(() => {
        if (!searchText.trim()) {
            setSearchResults([]);
            setSearchError(null);
            setHasSearched(false);
            return;
        }

        // Resetar hasSearched quando usuário está digitando
        setHasSearched(false);

        const timeoutId = setTimeout(() => {
            performSearch(searchText.trim());
        }, 1200); // 1200ms de delay - aguarda usuário terminar de digitar

        return () => clearTimeout(timeoutId);
    }, [searchText]);

    // Monitorar mudanças no array de mensagens para detectar resposta da API
    useEffect(() => {
        if (isSubmitting && messages && messages.length > initialMessageCount) {
            // Nova mensagem foi adicionada, verificar se é uma resposta do bot
            const lastMessage = messages[messages.length - 1];
            if (lastMessage && lastMessage.origin === 'bot' && !lastMessage.typing) {
                // Resposta do bot recebida, limpar timeout e parar o loading
                if (submitTimeoutId) {
                    clearTimeout(submitTimeoutId);
                    setSubmitTimeoutId(null);
                }
                setIsSubmitting(false);
                closeModal();
            }
        }
    }, [messages, isSubmitting, initialMessageCount, submitTimeoutId]);

    // Controlar a animação do modal quando é aberto
    useEffect(() => {
        if (isModalOpen) {
            // Inicia a animação após um pequeno delay
            setTimeout(() => {
                setVisible(true);
            }, 10);

            // Controlar scroll do body
            document.body.classList.add('modal-open');
            document.body.style.overflow = 'hidden';
        } else {
            setVisible(false);
            document.body.classList.remove('modal-open');
            document.body.style.overflow = 'unset';
        }

        // Cleanup quando componente é desmontado
        return () => {
            document.body.classList.remove('modal-open');
            document.body.style.overflow = 'unset';
        };
    }, [isModalOpen]);

    // Cleanup do timeout quando componente é desmontado
    useEffect(() => {
        return () => {
            if (submitTimeoutId) {
                clearTimeout(submitTimeoutId);
            }
        };
    }, [submitTimeoutId]);

    // Função para buscar exames na API
    const performSearch = async (searchTerm) => {
        if (!searchTerm) return;

        setIsSearching(true);
        setSearchError(null);

        try {
            const data = await authenticatedFetch(`${getApiBaseUrl()}/search`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    exam: searchTerm,
                    brandInitials: "AM",
                    exactMatch: true,
                    regional: "SP",
                    sortType: "RELEVANCE",
                    order: "desc"
                })
            });

            console.log(data);
            const exams = Array.isArray(data?.exams) ? data.exams : [];
            setSearchResults(exams);
        } catch (error) {
            console.error('Erro ao buscar procedimentos:', error);
            setSearchError('Erro ao buscar procedimentos. Tente novamente.');
            setSearchResults([]);
        } finally {
            setIsSearching(false);
            setHasSearched(true); // Marca que uma busca foi realizada
        }
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const handleButtonLink = () => {
        const text = buttonLinkText || '';
        if (!text) return;
        if (typeof onButtonLinkClick === 'function') {
            onButtonLinkClick(text);
        }
        // Se o modal estiver aberto, fechá-lo
        if (isModalOpen) {
            closeModal();
        }
    };

    const closeModal = () => {
        setVisible(false);
        // Limpar timeout se existir
        if (submitTimeoutId) {
            clearTimeout(submitTimeoutId);
            setSubmitTimeoutId(null);
        }
        // Aguarda a animação antes de fechar completamente
        setTimeout(() => {
            setIsModalOpen(false);
            setSearchText('');
            setSearchResults([]);
            // Não limpar selectedExams para manter os exames pré-existentes
            setSearchError(null);
            setIsSubmitting(false);
            setInitialMessageCount(0);
            setHasSearched(false);
        }, 300);
    };

    const handleSearch = () => {
        if (selectedExams.length > 0) {
            // Iniciar loading customizado na tela do chat
            setIsSubmitting(true);

            // Adicionar indicador de loading customizado ("Adicionando exames...")
            if (setMessages) {
                const customTyping = {
                    origin: 'bot',
                    response: 'Digitando...',
                    options: [],
                    typing: true,
                    isBuscaExamesLoading: true
                };
                setMessages(prev => [...prev, customTyping]);
            }

            // Fechar o modal imediatamente
            setIsModalOpen(false);
            setVisible(false);

            setInitialMessageCount(messages ? messages.length : 0);

            // Timeout de segurança caso a API não responda
            const timeoutId = setTimeout(() => {
                console.warn('Timeout ao aguardar resposta da API');
                setIsSubmitting(false);
                closeModal();
            }, 30000); // 30 segundos
            setSubmitTimeoutId(timeoutId);

            // Montar o objeto de exames selecionados conforme especificado
            const examesSelecionados = {
                procedimentos: selectedExams.map(exam => ({
                    id: exam.id,
                    nome: exam.name,
                    valor: parseFloat(exam.price) || 0,
                    // Garantir que as flags de cobertura enviadas para a API
                    // reflitam corretamente os dados do exame (vindo da busca manual ou do contexto)
                    hasMobileCoverage:
                        typeof exam.hasMobileCoverage === 'boolean'
                            ? exam.hasMobileCoverage
                            : (typeof exam.isMobileCoverage === 'boolean'
                                ? exam.isMobileCoverage
                                : false),
                    hasUnitCoverage:
                        typeof exam.hasUnitCoverage === 'boolean'
                            ? exam.hasUnitCoverage
                            : (typeof exam.isUnitCoverage === 'boolean'
                                ? exam.isUnitCoverage
                                : false)
                })),
                valorTotal: selectedExams.reduce((total, exam) => {
                    return total + (parseFloat(exam.price) || 0);
                }, 0)
            };

            console.log('Exames selecionados:', examesSelecionados);

            // Fazer chamada da API com os dados no contexto apenas se temos os parâmetros necessários
            if (client_id && setMessages) {
                // Enviar mensagem vazia com contexto dos exames selecionados
                sendMessage(
                    setMessages,
                    client_id,
                    '', // Mensagem vazia
                    messages,
                    marca,
                    origem,
                    null, // teste
                    localizacao,
                    null, // latitude
                    null, // longitude
                    null, // lastInput
                    null, // setTeste
                    null, // getLocation
                    operational_system,
                    version_app,
                    examesSelecionados, // Passar os exames selecionados como parâmetro adicional
                    null, // additionalContext
                    requestImage, // requestImage
                    setRequestId // setRequestId
                );
            } else {
                console.warn('Parâmetros insuficientes para fazer chamada da API:', {
                    client_id,
                    setMessages: !!setMessages
                });
                // Se não conseguir enviar, limpar timeout e parar o loading
                if (submitTimeoutId) {
                    clearTimeout(submitTimeoutId);
                    setSubmitTimeoutId(null);
                }
                setIsSubmitting(false);
                closeModal();
            }

            if (onSearchClick) {
                onSearchClick(selectedExams);
            }

            // O modal já foi fechado no início do handleSearch.
        }
    };

    const handleExamSelection = (exam, isChecked) => {
        // Não permitir seleção de exames desabilitados
        if (isExamDisabled(exam)) {
            return;
        }

        if (isChecked) {
            // Marcar novos exames como não originais
            const newExam = { ...exam, isOriginal: false };
            setSelectedExams(prev => [...prev, newExam]);

            // Após marcar um exame, limpar a caixa de texto da pesquisa
            setSearchText('');
        } else {
            // Verificar se a remoção é permitida
            if (!canRemoveExam(exam.id)) {
                return;
            }
            setSelectedExams(prev => prev.filter(selected => selected.id !== exam.id));
        }
    };

    // Função para verificar se um exame pode ser removido
    const canRemoveExam = (examId) => {
        // Se a flag agendamentoAdicionaProcedimentos estiver ativa
        if (message?.agendamentoAdicionaProcedimentos) {
            // Verificar se o exame é original (não pode ser removido)
            const isOriginalExam = originalExams.some(exam => exam.id === examId);
            return !isOriginalExam; // Só pode remover se NÃO for original
        }
        // Se a flag não estiver ativa, pode remover qualquer exame
        return true;
    };

    const isExamSelected = (examId) => {
        return selectedExams.some(exam => exam.id === examId);
    };

    const isExamDisabled = (exam) => {
        // Se buscaExamesMovel for true, desabilitar exames que não têm cobertura móvel
        if (buscaExamesMovel && !exam.isMobileCoverage) {
            return true;
        }

        // Desabilitar exames que não têm cobertura de agendamento digital (somente telefone)
        if (exam.isAdCoverage === false) {
            return true;
        }

        return false;
    };

    const getDisabledReason = (exam) => {
        if (buscaExamesMovel && !exam.isMobileCoverage) {
            return 'Este procedimento não está disponível para atendimento domiciliar';
        }

        if (exam.isAdCoverage === false) {
            return 'Agendamento somente por telefone';
        }

        return '';
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            if (selectedExams.length > 0) {
                handleSearch();
            }
        }
        if (e.key === 'Escape') {
            closeModal();
        }
    };

    return (
        <>
            {/* Componente principal - aparece no chat */}
            <div className="busca-exames-container">
                <button
                    className="busca-exames-button"
                    onClick={openModal}
                    aria-label="Abrir busca de procedimentos"
                >
                    <div className="busca-content">
                        <IoSearch className="search-icon" />
                        <span>
                            {selectedExams.length > 0
                                ? `Gerenciar procedimentos selecionados (${selectedExams.length})`
                                : 'Digite os procedimentos que deseja realizar'
                            }
                        </span>
                    </div>
                </button>

                {(buttonLink || showHomeLink) && (
                    <div className="busca-exames-buttonlink-wrapper">
                        {buttonLink && buttonLinkText && (
                            <ButtonLink
                                text={buttonLinkText}
                                onClick={handleButtonLink}
                            />
                        )}
                        {showHomeLink && (
                            <ButtonLink
                                text="Acessar menu inicial"
                                onClick={handleResetToHome}
                            />
                        )}
                    </div>
                )}
            </div>

            {/* Modal de busca - surge da base como CSAT */}
            {isModalOpen && (
                <>
                    <div
                        className={`modal-overlay ${visible ? 'visible' : ''}`}
                        onClick={closeModal}
                    ></div>
                    <div className={`modal-content-csat busca-exames-modal ${visible ? 'visible' : ''}`}>
                        <div className="modal-header">
                            <h3>Buscar procedimentos</h3>
                            <button
                                className="close-button"
                                onClick={closeModal}
                                aria-label="Fechar modal"
                            >
                                ×
                            </button>
                        </div>

                        <div className="modal-body">
                            {/* Mensagem informativa quando há procedimentos pré-carregados */}
                            {message?.examesSelecionados?.procedimentos && selectedExams.length > 0 && (
                                <div className="pre-loaded-notice">
                                    <span>
                                        {message?.agendamentoAdicionaProcedimentos
                                            ? "📋 Você pode adicionar novos procedimentos ao agendamento. Os procedimentos já agendados não podem ser removidos."
                                            : "📋 Procedimentos já selecionados carregados. Você pode adicionar mais procedimentos ou remover os existentes."
                                        }
                                    </span>
                                </div>
                            )}

                            <div className="search-input-container">
                                <IoSearch className="input-search-icon" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder="Digite os procedimentos que deseja realizar"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onKeyDown={handleKeyPress}
                                    className="search-input"
                                    onFocus={() => {
                                        window.scrollTo(0, 0);
                                    }}
                                    aria-label="Campo de busca de procedimentos"
                                />
                            </div>

                            {/* Indicador de carregamento */}
                            {isSearching && (
                                <div className="search-loading">
                                    <div className="loading-spinner"></div>
                                    <span>Buscando procedimentos...</span>
                                </div>
                            )}

                            {/* Erro de busca */}
                            {searchError && (
                                <div className="search-error">
                                    {searchError}
                                </div>
                            )}

                            {/* Resultados da busca */}
                            {searchResults.length > 0 && (
                                <div className="search-results">
                                    <h4>Resultados encontrados:</h4>
                                    {buscaExamesMovel && (
                                        <div className="mobile-search-notice">
                                            <span>🏠 Exibindo apenas procedimentos com atendimento domiciliar disponível</span>
                                        </div>
                                    )}
                                    <div className="exam-list">
                                        {searchResults.map((exam) => (
                                            <div key={exam.id} className={`exam-item ${isExamDisabled(exam) ? 'exam-item-disabled' : ''}`}>
                                                <label
                                                    className={`exam-checkbox-label ${isExamDisabled(exam) ? 'exam-label-disabled' : ''}`}
                                                    title={isExamDisabled(exam) ? getDisabledReason(exam) : ''}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isExamSelected(exam.id)}
                                                        onChange={(e) => !isExamDisabled(exam) && handleExamSelection(exam, e.target.checked)}
                                                        className="exam-checkbox"
                                                        disabled={isExamDisabled(exam)}
                                                    />
                                                    <div className="exam-info">
                                                        <div className="exam-details">
                                                            <div className="exam-name-row">
                                                                <span className="exam-name">{exam.name}</span>
                                                                {exam.isAdCoverage === false && (
                                                                    <span className="phone-only-tag">
                                                                        Agendamento por telefone
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="exam-coverage">
                                                                {exam.isMobileCoverage && (
                                                                    <div className="coverage-item" title="Atendimento domiciliar disponível">
                                                                        <IconHome className="coverage-icon" />
                                                                        <span className="coverage-text">Em casa</span>
                                                                    </div>
                                                                )}
                                                                <div className="coverage-item" title="Atendimento em unidades disponível">
                                                                    <IconUnit className="coverage-icon" />
                                                                    <span className="coverage-text">Na unidade</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {shouldShowPrices && (
                                                            <span className="exam-price">{formatCurrency(exam.price)}</span>
                                                        )}
                                                    </div>
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Mensagem quando não há resultados */}
                            {searchText.trim() && !isSearching && searchResults.length === 0 && !searchError && hasSearched && (
                                <div className="no-results">
                                    Nenhum procedimento encontrado para "{searchText}"
                                </div>
                            )}

                            {/* Procedimentos selecionados */}
                            {selectedExams.length > 0 && (
                                <div className="selected-exams">
                                    <h4>Procedimentos selecionados ({selectedExams.length}):</h4>
                                    <div className="selected-list">
                                        {(() => {
                                            // Separar procedimentos novos e originais apenas se há flag de agendamento
                                            if (message?.agendamentoAdicionaProcedimentos) {
                                                const newExams = selectedExams.filter(exam => !exam.isOriginal);
                                                const originalExams = selectedExams.filter(exam => exam.isOriginal);

                                                // Ordenar alfabeticamente cada grupo
                                                const sortedNewExams = newExams.sort((a, b) => a.name.localeCompare(b.name));
                                                const sortedOriginalExams = originalExams.sort((a, b) => a.name.localeCompare(b.name));

                                                return (
                                                    <>
                                                        {/* Novos exames primeiro */}
                                                        {sortedNewExams.map((exam) => (
                                                            <div key={exam.id} className="selected-exam">
                                                                <div className="selected-exam-info">
                                                                    <div className="selected-exam-details">
                                                                        <div className="selected-exam-name-row">
                                                                            <span className="selected-exam-name">{exam.name}</span>
                                                                        </div>
                                                                        {shouldShowPrices && (
                                                                            <span className="selected-exam-price">{formatCurrency(exam.price)}</span>
                                                                        )}
                                                                    </div>
                                                                    <div className="selected-exam-coverage">
                                                                        {exam.isMobileCoverage && (
                                                                            <div className="coverage-item" title="Atendimento domiciliar disponível">
                                                                                <IconHome className="coverage-icon" />
                                                                                <span className="coverage-text">Em casa</span>
                                                                            </div>
                                                                        )}
                                                                        <div className="coverage-item" title="Atendimento em unidades disponível">
                                                                            <IconUnit className="coverage-icon" />
                                                                            <span className="coverage-text">Na unidade</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    className="remove-exam"
                                                                    onClick={() => handleExamSelection(exam, false)}
                                                                    aria-label={`Remover ${exam.name}`}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        ))}

                                                        {/* Separador visual se há exames originais */}
                                                        {sortedOriginalExams.length > 0 && sortedNewExams.length > 0 && (
                                                            <div className="exams-separator">
                                                                <span className="separator-text">Procedimentos já agendados</span>
                                                            </div>
                                                        )}

                                                        {/* Procedimentos originais por último */}
                                                        {sortedOriginalExams.map((exam) => (
                                                            <div key={exam.id} className="selected-exam selected-exam-locked">
                                                                <div className="selected-exam-info">
                                                                    <div className="selected-exam-details">
                                                                        <div className="selected-exam-name-row">
                                                                            <span className="selected-exam-name">{exam.name}</span>
                                                                            <span className="exam-locked-tag">Agendado</span>
                                                                        </div>
                                                                        {shouldShowPrices && (
                                                                            <span className="selected-exam-price">{formatCurrency(exam.price)}</span>
                                                                        )}
                                                                    </div>
                                                                    <div className="selected-exam-coverage">
                                                                        {exam.isMobileCoverage && (
                                                                            <div className="coverage-item" title="Atendimento domiciliar disponível">
                                                                                <IconHome className="coverage-icon" />
                                                                                <span className="coverage-text">Em casa</span>
                                                                            </div>
                                                                        )}
                                                                        <div className="coverage-item" title="Atendimento em unidades disponível">
                                                                            <IconUnit className="coverage-icon" />
                                                                            <span className="coverage-text">Na unidade</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <span
                                                                    className="remove-exam-disabled"
                                                                    title="Este procedimento não pode ser removido"
                                                                >
                                                                    🔒
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </>
                                                );
                                            } else {
                                                // Comportamento normal quando não há flag de agendamento
                                                return selectedExams
                                                    .sort((a, b) => a.name.localeCompare(b.name))
                                                    .map((exam) => (
                                                        <div key={exam.id} className="selected-exam">
                                                            <div className="selected-exam-info">
                                                                <div className="selected-exam-details">
                                                                    <div className="selected-exam-name-row">
                                                                        <span className="selected-exam-name">{exam.name}</span>
                                                                    </div>
                                                                    {shouldShowPrices && (
                                                                        <span className="selected-exam-price">{formatCurrency(exam.price)}</span>
                                                                    )}
                                                                </div>
                                                                <div className="selected-exam-coverage">
                                                                    {exam.isMobileCoverage && (
                                                                        <div className="coverage-item" title="Atendimento domiciliar disponível">
                                                                            <IconHome className="coverage-icon" />
                                                                            <span className="coverage-text">Em casa</span>
                                                                        </div>
                                                                    )}
                                                                    <div className="coverage-item" title="Atendimento em unidades disponível">
                                                                        <IconUnit className="coverage-icon" />
                                                                        <span className="coverage-text">Na unidade</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <button
                                                                className="remove-exam"
                                                                onClick={() => handleExamSelection(exam, false)}
                                                                aria-label={`Remover ${exam.name}`}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ));
                                            }
                                        })()}
                                    </div>

                                    {/* Total dos exames selecionados */}
                                    <div className="selected-total">
                                        <span className="total-label">Total:</span>
                                        <span className="total-price">
                                            {formatCurrency(
                                                shouldShowPrices
                                                    ? selectedExams.reduce((total, exam) => {
                                                        return total + (parseFloat(exam.price) || 0);
                                                    }, 0)
                                                    : 0
                                            )}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            {(buttonLink || showHomeLink) && (
                                <div className="busca-exames-buttonlink-wrapper busca-exames-buttonlink-wrapper--modal">
                                    {buttonLink && buttonLinkText && (
                                        <ButtonLink
                                            text={buttonLinkText}
                                            onClick={handleButtonLink}
                                        />
                                    )}
                                    {showHomeLink && (
                                        <ButtonLink
                                            text="Acessar menu inicial"
                                            onClick={handleResetToHome}
                                        />
                                    )}
                                </div>
                            )}
                            <button
                                className={`add-to-cart-button ${isSubmitting ? 'loading' : ''}`}
                                onClick={handleSearch}
                                disabled={selectedExams.length === 0 || isSubmitting}
                                aria-label="Adicionar procedimentos à sacola"
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="loading-spinner"></div>
                                        <span>Finalizando seleção...</span>
                                    </>
                                ) : (
                                    message?.examesSelecionados?.procedimentos
                                        ? `Atualizar seleção (${selectedExams.length})`
                                        : `Continuar agendamento (${selectedExams.length})`
                                )}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default BuscaExames;
